import React, { useState, useCallback } from 'react';
import { DocumentMagnifyingGlassIcon, CloudArrowUpIcon, ArrowPathIcon, EnvelopeIcon, ClockIcon, DocumentArrowDownIcon, TableCellsIcon, CogIcon } from '@heroicons/react/24/outline';

// ====================================================================
// Mock Data Store & Constants
// ====================================================================

const initialTaxSettings = {
    taxFormType: 'Form-16 (India)',
    financialYear: '2024-2025',
    defaultEmailSubject: 'Your Annual Tax Document (Form-16) is Ready',
    autoSubmissionToGovt: false,
    digitalSignatureEnabled: true,
};

const MOCK_ACTIVITY_LOGS = [
    { id: 1, employeeId: "EMP001", action: "Form-16 Generated", modifiedBy: "Tax Admin", date: "2024-01-15", details: "Annual Form-16 generated for FY 2023-24" },
    { id: 2, employeeId: "EMP002", action: "TDS Certificate Updated", modifiedBy: "Finance Manager", date: "2024-01-14", details: "TDS certificate updated with revised deductions" },
    { id: 3, employeeId: "EMP003", action: "Tax Settings Modified", modifiedBy: "HR Admin", date: "2024-01-13", details: "Digital signature settings enabled" },
    { id: 4, employeeId: "EMP001", action: "Auto Submission Enabled", modifiedBy: "System Admin", date: "2024-01-12", details: "Automatic government submission activated" },
    { id: 5, employeeId: "EMP004", action: "Form Validation Failed", modifiedBy: "Tax System", date: "2024-01-11", details: "Form validation failed - missing PAN details" },
];

// ====================================================================
// AutoTaxForm16Setup (Main Component)
// ====================================================================

const AutoTaxForm16Setup = () => {
    const [settings, setSettings] = useState(initialTaxSettings);
    const [isSaving, setIsSaving] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [activeTab, setActiveTab] = useState('setup');

    const handleInputChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    }, []);

    const handleSave = (e) => {
        e.preventDefault();
        setIsSaving(true);
        // Simulate API call
        setTimeout(() => {
            console.log('Saving Tax Settings:', settings);
            setIsSaving(false);
            alert('Annual Tax Configuration Saved Successfully!');
        }, 1500);
    };

    const handleGenerateForms = useCallback(() => {
        setIsGenerating(true);
        // Simulate report generation
        setTimeout(() => {
            setIsGenerating(false);
            alert(`Tax forms (${settings.taxFormType}) successfully generated for ${settings.financialYear}! Ready for review.`);
        }, 4000);
    }, [settings.taxFormType, settings.financialYear]);

    // Helper for a switch toggle
    const SwitchToggle = ({ name, checked, onChange, label, description }) => (
        <div className="flex flex-col p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-800">{label}</span>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        name={name}
                        checked={checked}
                        onChange={onChange}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">{description}</p>
        </div>
    );

    const InputField = ({ name, label, value, onChange, type = 'text', icon: Icon }) => (
        <div>
            <label htmlFor={name} className="block text-xs font-medium text-gray-600">{label}</label>
            <div className="relative mt-1 rounded-md shadow-sm">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <Icon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <input
                    type={type}
                    name={name}
                    id={name}
                    value={value}
                    onChange={onChange}
                    className="block w-full rounded-md border-gray-300 pl-10 focus:border-indigo-500 focus:ring-indigo-500 text-sm h-10 p-2"
                />
            </div>
        </div>
    );

    // Activity Logs Component
    const ActivityLogs = () => (
        <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-700 border-b pb-2 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Activity Logs
            </h2>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ID</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modified By</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {MOCK_ACTIVITY_LOGS.map((log) => (
                            <tr key={log.id} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{log.employeeId}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{log.action}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{log.modifiedBy}</td>
                                <td className="px-4 py-3 text-sm text-gray-700">{log.date}</td>
                                <td className="px-4 py-3 text-sm text-gray-600">{log.details}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    // Reports Component
    const Reports = () => {
        const [reportType, setReportType] = useState('daily');
        const [customStartDate, setCustomStartDate] = useState('');
        const [customEndDate, setCustomEndDate] = useState('');
        const [isDownloading, setIsDownloading] = useState(false);

        const reportOptions = [
            { label: "Daily", value: "daily" },
            { label: "Monthly", value: "monthly" },
            { label: "Quarterly", value: "quarterly" },
            { label: "Half Yearly", value: "half-yearly" },
            { label: "Yearly", value: "yearly" },
            { label: "Custom Date Range", value: "custom" },
        ];

        const handleDownload = (format) => {
            setIsDownloading(true);
            setTimeout(() => {
                setIsDownloading(false);
                alert(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} Tax Report downloaded in ${format.toUpperCase()} format!`);
            }, 2000);
        };

        return (
            <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-xl font-semibold text-gray-700 border-b pb-2 mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    Tax Reports
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Report Type</label>
                        <select
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-indigo-500 focus:border-indigo-500"
                        >
                            {reportOptions.map((option) => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                        </select>
                    </div>
                    
                    {reportType === 'custom' && (
                        <>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={customStartDate}
                                    onChange={(e) => setCustomStartDate(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={customEndDate}
                                    onChange={(e) => setCustomEndDate(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                    <button
                        onClick={() => handleDownload('excel')}
                        disabled={isDownloading}
                        className={`px-6 py-3 text-sm font-semibold rounded-lg shadow-lg flex items-center justify-center bg-green-600 text-white hover:bg-green-700 focus:ring-4 focus:ring-green-300 ${isDownloading ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                        {isDownloading ? (
                            <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                        ) : (
                            <TableCellsIcon className="h-5 w-5 mr-2" />
                        )}
                        {isDownloading ? 'Generating...' : 'Download Excel'}
                    </button>
                    
                    <button
                        onClick={() => handleDownload('pdf')}
                        disabled={isDownloading}
                        className={`px-6 py-3 text-sm font-semibold rounded-lg shadow-lg flex items-center justify-center bg-red-600 text-white hover:bg-red-700 focus:ring-4 focus:ring-red-300 ${isDownloading ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                        {isDownloading ? (
                            <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
                        ) : (
                            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                        )}
                        {isDownloading ? 'Generating...' : 'Download PDF'}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div 
            className="min-h-screen p-4 sm:p-6 lg:p-8 bg-gray-50"
            style={{ fontFamily: 'Roboto, sans-serif' }}
        >
            <header className="flex justify-between items-center pb-6 mb-8 border-b border-gray-200">
                <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <DocumentMagnifyingGlassIcon className="w-8 h-8 inline-block mr-2 text-indigo-600"/> Auto Tax / Form-16 Setup
                </h1>
            </header>

            <div className="max-w-4xl mx-auto space-y-8">
                
                {/* Tab Navigation */}
                <div className="bg-white p-1 rounded-xl shadow-lg border border-gray-100">
                    <div className="flex space-x-1">
                        <button
                            onClick={() => setActiveTab('setup')}
                            className={`px-6 py-3 text-sm font-semibold rounded-lg transition duration-300 flex items-center ${
                                activeTab === 'setup'
                                    ? 'bg-indigo-600 text-white shadow-lg'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            <CogIcon className="h-5 w-5 mr-2" />
                            Tax Setup
                        </button>
                        <button
                            onClick={() => setActiveTab('activity')}
                            className={`px-6 py-3 text-sm font-semibold rounded-lg transition duration-300 flex items-center ${
                                activeTab === 'activity'
                                    ? 'bg-indigo-600 text-white shadow-lg'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            <ClockIcon className="h-5 w-5 mr-2" />
                            Activity Logs
                        </button>
                        <button
                            onClick={() => setActiveTab('reports')}
                            className={`px-6 py-3 text-sm font-semibold rounded-lg transition duration-300 flex items-center ${
                                activeTab === 'reports'
                                    ? 'bg-indigo-600 text-white shadow-lg'
                                    : 'text-gray-600 hover:bg-gray-100'
                            }`}
                        >
                            <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                            Reports
                        </button>
                    </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'setup' && (
                    <>
                        {/* 1. Configuration Settings */}
                <form onSubmit={handleSave} className="bg-white rounded-xl shadow-lg p-6 space-y-6">
                    <h2 className="text-xl font-semibold text-gray-700 border-b pb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        Compliance & Form Settings
                    </h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="taxFormType" className="block text-xs font-medium text-gray-600">Primary Tax Form Type</label>
                            <select
                                name="taxFormType"
                                id="taxFormType"
                                value={settings.taxFormType}
                                onChange={handleInputChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-10 p-2"
                            >
                                <option>Form-16 (India)</option>
                                <option>W-2 (USA)</option>
                                <option>T4 (Canada)</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="financialYear" className="block text-xs font-medium text-gray-600">Applicable Financial Year</label>
                            <input
                                type="text"
                                name="financialYear"
                                id="financialYear"
                                value={settings.financialYear}
                                onChange={handleInputChange}
                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm h-10 p-2"
                            />
                        </div>
                    </div>

                    <InputField 
                        name="defaultEmailSubject" 
                        label="Payslip/Form Email Subject" 
                        value={settings.defaultEmailSubject} 
                        onChange={handleInputChange} 
                        icon={EnvelopeIcon}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                        <SwitchToggle
                            name="autoSubmissionToGovt"
                            checked={settings.autoSubmissionToGovt}
                            onChange={handleInputChange}
                            label="Enable Auto Submission"
                            description="Automatically file regulatory documents (e.g., TDS returns) after finalization."
                        />
                        <SwitchToggle
                            name="digitalSignatureEnabled"
                            checked={settings.digitalSignatureEnabled}
                            onChange={handleInputChange}
                            label="Digital Signature"
                            description="Use the pre-uploaded digital signature for bulk signing of tax forms."
                        />
                    </div>

                    <div className="flex justify-end pt-4">
                        <button
                            type="submit"
                            disabled={isSaving}
                            className={`flex items-center space-x-2 px-6 py-3 text-lg font-semibold rounded-lg shadow-md transition transform ${isSaving ? 'bg-gray-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 active:scale-95 text-white'}`}
                        >
                            {isSaving ? (
                                <ArrowPathIcon className="w-5 h-5 animate-spin"/>
                            ) : (
                                <CloudArrowUpIcon className="w-5 h-5"/>
                            )}
                            <span>{isSaving ? 'Saving...' : 'Save Tax Settings'}</span>
                        </button>
                    </div>
                </form>

                        {/* 2. Generation Action */}
                        <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col sm:flex-row justify-between items-center border-l-4 border-green-500">
                            <div className="space-y-1 sm:pr-6">
                                <h2 className="text-xl font-semibold text-gray-700" style={{ fontFamily: 'Poppins, sans-serif' }}>
                                    Generate Annual Tax Forms
                                </h2>
                                <p className="text-sm text-gray-600">
                                    Click to generate all employee tax documents for the selected financial year based on current payroll data.
                                </p>
                            </div>
                            <button
                                onClick={handleGenerateForms}
                                disabled={isGenerating}
                                className={`flex items-center space-x-2 px-5 py-2.5 text-sm font-semibold rounded-lg shadow-md transition mt-4 sm:mt-0 w-full sm:w-auto ${isGenerating ? 'bg-yellow-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 active:scale-95 text-white'}`}
                            >
                                {isGenerating ? (
                                    <ArrowPathIcon className="w-5 h-5 animate-spin"/>
                                ) : (
                                    <DocumentMagnifyingGlassIcon className="w-5 h-5"/>
                                )}
                                <span>{isGenerating ? 'Generating...' : `Generate ${settings.taxFormType}`}</span>
                            </button>
                        </div>
                    </>
                )}
                
                {activeTab === 'activity' && <ActivityLogs />}
                {activeTab === 'reports' && <Reports />}
            </div>
        </div>
    );
};

export default AutoTaxForm16Setup;